import { clinicApiPath as buildClinicApiPath } from '@/shared/lib/apiPath';
import { apiClient } from '@/shared/lib/axios';
import { getStoredClinicContext } from '@/hooks/main/useClinicContext';
import type {
  InsurerData,
  InsurerSearchResponse,
  AddressData,
  MedicalAidData,
  CertificationData,
  DuplicateSearchResponse,
  ComparedPatient,
  CopyDataResponse,
  CopyCertificationPayload,
  CertificationListItem,
  CertificationDetailResponse,
  FormDataForComparison,
} from './types';

/**
 * Build clinic API base path from auth context (not URL).
 * Backend expects api/v1/clinic/{site_type}/{clinic_number}/...
 * URL segments like /medix/patients/... use "patients" as route name, not clinic_number;
 * real clinic_number comes from auth (e.g. CL001).
 */
const getClinicApiPath = (subPath: string): string => {
  const ctx = getStoredClinicContext();
  if (!ctx) return '';
  return buildClinicApiPath(ctx.tenant, ctx.clinicNumber, subPath);
};

export const certificationApi = {
  /**
   * Get certification list (資格情報一覧)
   * GET /api/v1/clinic/{site_type}/{clinic_number}/patients/certification/list
   * @param params - date_from and date_to filters
   */
  async getCertificationList(
    params?: { date_from?: string; date_to?: string }
  ): Promise<CertificationListItem[]> {
    const path = getClinicApiPath('/patients/certification/list');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.get<{
      success: boolean;
      data: { certifications: CertificationListItem[] };
    }>(path, { params });
    return res.data.data.certifications;
  },

  /**
   * Get certification detail by ID
   * GET /api/v1/clinic/{site_type}/{clinic_number}/patients/certification/{certification_id}
   */
  async getCertificationDetail(certificationId: number): Promise<CertificationDetailResponse> {
    const path = getClinicApiPath(`/patients/certification/${certificationId}`);
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.get<{ success: boolean; data: CertificationDetailResponse }>(path);
    return res.data.data;
  },

  /**
   * Search for insurer information by insurer number.
   * POST /api/v1/clinic/{site_type}/{clinic_number}/insurers/certification/search
   */
  async searchInsurers(insurerNumber: string): Promise<InsurerData> {
    const path = getClinicApiPath('/insurers/certification/search');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.post<{ success: boolean; data: InsurerSearchResponse }>(
      path,
      { insurer_number: insurerNumber }
    );
    
    if (!res.data.data.response || !res.data.data.health_insurance_cards_data) {
      throw new Error('保険者が見つかりません。');
    }
    return res.data.data.health_insurance_cards_data;
  },

  /**
   * Search for duplicate patients by sex and date of birth.
   * POST /api/v1/clinic/{site_type}/{clinic_number}/patients/certification/search
   */
  async searchDuplicatePatients(sexId: number, dateOfBirth: string): Promise<DuplicateSearchResponse> {
    const path = getClinicApiPath('/patients/certification/search');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.post<{ success: boolean; data: DuplicateSearchResponse }>(
      path,
      { sex_id: sexId, date_of_birth: dateOfBirth }
    );
    return res.data.data;
  },

  /**
   * Get patients for duplicate selection modal.
   * GET /api/v1/clinic/{site_type}/{clinic_number}/patients/certification/patients-modal
   */
  async getDuplicatePatients(
    patientIds: number[],
    formData?: FormDataForComparison
  ): Promise<ComparedPatient[]> {
    const path = getClinicApiPath('/patients/certification/patients-modal');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.get<{
      success: boolean;
      data: { patients: ComparedPatient[] };
    }>(path, {
      params: { patient_ids: patientIds.join('-'), ...formData },
    });
    return res.data.data.patients;
  },

  /**
   * Copy certification data to patient edit screen.
   * POST /api/v1/clinic/{site_type}/{clinic_number}/patients/certification/copy/{patient_id?}
   */
  async copyCertificationData(payload: CopyCertificationPayload, patientId: number = 0): Promise<CopyDataResponse> {
    const path = getClinicApiPath(`/patients/certification/copy/${patientId}`);
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.post<{ success: boolean; data: CopyDataResponse }>(path, payload);
    return res.data.data;
  },

  /**
   * Search for address by postal code.
   * POST /api/v1/clinic/{site_type}/{clinic_number}/addresses/lookup
   */
  async searchAddresses(postalCode: string): Promise<AddressData> {
    const path = getClinicApiPath('/addresses/lookup');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.post<{ success: boolean; data: AddressData }>(path, {
      postal_code: postalCode,
    });
    return res.data.data;
  },

  /**
   * Search for medical aid information.
   * This opens a modal in the original system - keeping as placeholder
   */
  async searchMedicalAids(insurerTypeId: number, medicalAidNumber: string): Promise<MedicalAidData> {
    const path = getClinicApiPath(`/insurers/medical-search/${insurerTypeId}/${medicalAidNumber}`);
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.get<{ success: boolean; data: MedicalAidData }>(path);
    return res.data.data;
  },

  /**
   * Get full certification information from an external source.
   * This opens a modal in the original system - keeping as placeholder
   */
  async getCertification(params: Record<string, unknown>): Promise<CertificationData> {
    const path = getClinicApiPath('/patients/certification/modal');
    if (!path) throw new Error('Clinic context not available');
    const res = await apiClient.post<{ success: boolean; data: CertificationData }>(path, params);
    return res.data.data;
  },
};
